/**
 * O cliente do servico `identidade` — entrar, renovar, sair, quem sou eu, recuperar senha,
 * gerir usuarios, gerir perfis e as permissoes de cada perfil e ler a trilha de auditoria.
 *
 * O modulo espelha o servico, um a um. As credenciais **nunca** passam por aqui de volta:
 * o JWT e o token de renovacao viajam so em cookie `HttpOnly` (AD-5), escritos e apagados
 * pelo proprio servico. Este arquivo so envia a credencial de entrada e devolve quem
 * entrou.
 *
 * `entrar`, `renovar` e `sair` usam `semRenovacao`: renovar uma rota de sessao seria
 * recursao, e um login recusado nao e sessao expirada. `sessaoAtual` **nao** usa — ela e
 * uma chamada como as outras, e um acesso expirado com sessao viva deve se resolver
 * sozinho, sem devolver o usuario a tela de login.
 *
 * As duas rotas de senha (`solicitarRedefinicaoDeSenha`, `redefinirSenha`) tambem usam
 * `semRenovacao`: sao **publicas**, existem justamente para quem nao consegue entrar, e
 * tentar renovar sessao nelas seria uma chamada extra que nunca poderia dar certo.
 *
 * As funcoes de gestao de acesso (`criarUsuario`, `listarUsuarios`, `desativarUsuario`,
 * `trocarPerfilDoUsuario`, as de perfis e `lerTrilhaDeAuditoria`) **nao** passam
 * `semRenovacao`: sao chamadas autenticadas comuns, e um acesso expirado no meio do
 * trabalho do Admin deve se resolver sozinho.
 *
 * A trilha de auditoria tem **so leitura** aqui, e isso e o contrato e nao um recorte: a
 * tabela e append-only por construcao, e nao existe rota de escrita nem de remocao do
 * outro lado.
 *
 * A tela de login, a de "esqueci minha senha" e a de nova senha entraram com o shell, na
 * historia 1.9 (`src/rotas/`); a sessao vive em `sessao.ts`, ao lado. As telas Usuarios e
 * Permissoes nasceram na 11.1 (`Usuarios.tsx`, `Permissoes.tsx`); a da trilha continua futura.
 *
 * `listarResponsaveis` e a excecao a "gestao de acesso": ela atende **qualquer** perfil, porque
 * o FR-32 concede a troca de Responsavel aos quatro. Ela e a unica funcao deste modulo com hook
 * proprio (`useResponsaveis`), porque e a unica que uma tela de outro servico consome.
 *
 * **Nenhum tipo de contrato e digitado neste arquivo** (AD-16): todos sao apelidos dos
 * schemas gerados em `src/api/tipos-gerados/identidade.ts`. Alterou o contrato do servico,
 * rode `npm run gerar-tipos` — o CI reprova a divergencia.
 */

import { useQuery } from '@tanstack/react-query'

import { CAMINHO_DA_RENOVACAO, chamar } from '@/api/cliente'
import type { components } from '@/api/tipos-gerados/identidade'

/**
 * Os schemas do `openapi.json` da `identidade`, gerados por `npm run gerar-tipos` (AD-16).
 *
 * Os tipos abaixo sao **apelidos** deles, e nao declaracoes: e o que impede o contrato de
 * viver duas vezes, uma em Python e outra digitada aqui (DW-17, DW-30). O apelido existe
 * porque o nome do schema e o do transporte (`RespostaDeSessao`) e o nome do dominio e
 * outro (`Sessao`) — quem chama nao deve precisar saber o primeiro.
 */
type Esquemas = components['schemas']

/** Os quatro perfis do sistema (AD-18) — o enum fechado, como o servico o publica. */
export type Perfil = Esquemas['Perfil']

/**
 * A mesma lista, como **valor** de runtime.
 *
 * O tipo acima e apagado na compilacao, e uma tela que precise percorrer os perfis — um
 * seletor, um filtro — precisa deles em tempo de execucao. O mapa intermediario e o que
 * amarra os dois lados: `Record<Perfil, true>` exige **todas** as chaves do enum e recusa
 * qualquer uma que nao seja dele. Perfil novo no backend, ou perfil renomeado, quebra o
 * build aqui — em vez de a lista envelhecer em silencio, que e o DW-17.
 */
const TODOS_OS_PERFIS: Record<Perfil, true> = {
  admin: true,
  gestor_comercial: true,
  vendedor_closer: true,
  sdr: true,
}

/** Os quatro, na ordem em que foram escritos acima. */
export const PERFIS = Object.keys(TODOS_OS_PERFIS) as readonly Perfil[]

/**
 * Os dois estados gravados do catalogo (FR-61-A, historia 11.1): `concedida` e `negada`. O ✳
 * ("concedida, limitada a propria carteira") nao e estado: e derivado de concedida + escopo
 * `propria`, e chega pronto em `limitada` de cada linha do mapa de um perfil.
 */
export type EstadoDaPermissao = Esquemas['EstadoDaPermissao']

/** O que a claim do JWT carrega: so as **concedidas**, porque ausencia e negacao. */
export type EstadoNaClaim = Exclude<EstadoDaPermissao, 'negada'>

/**
 * Permissoes do usuario: `nome -> estado`. So as **concedidas** viajam — ausencia e
 * negacao. Nunca use esta lista como autorizacao: esconder botao e conveniencia, a
 * checagem de verdade e do backend.
 *
 * E `Sessao['permissoes']`, e nao um `Record<string, EstadoNaClaim>` digitado aqui: o
 * contrato servido diz `dict[str, str]` (o valor nao e um enum do lado do schema), e um
 * tipo local mais estreito seria uma promessa que o backend nunca fez. Estreite no ponto
 * de uso, com `EstadoNaClaim`, quando precisar comparar.
 */
export type Permissoes = Sessao['permissoes']

/** Quem esta autenticado. E o mesmo corpo de entrar, renovar e `sessoes/atual`. */
export type Sessao = Esquemas['RespostaDeSessao']

/** Credencial de entrada: e-mail e senha, e nada mais. */
export type Credenciais = Esquemas['CorpoDeEntrada']

/** Entra com e-mail e senha. Os dois cookies vem na resposta, fora do corpo. */
export function entrar(credenciais: Credenciais): Promise<Sessao> {
  return chamar<Sessao>('identidade', '/sessoes', {
    method: 'POST',
    corpo: credenciais,
    semRenovacao: true,
  })
}

/** Troca o token de renovacao por um JWT novo; o token e rotacionado no servidor. */
export function renovar(): Promise<Sessao> {
  return chamar<Sessao>('identidade', CAMINHO_DA_RENOVACAO, {
    method: 'POST',
    semRenovacao: true,
  })
}

/** Sai. Idempotente: sair sem sessao tambem responde `204`. */
export function sair(): Promise<void> {
  return chamar<void>('identidade', '/sessoes', { method: 'DELETE', semRenovacao: true })
}

/** Quem esta no request agora. `401` so quando nao ha sessao alguma para renovar. */
export function sessaoAtual(): Promise<Sessao> {
  return chamar<Sessao>('identidade', '/sessoes/atual')
}

/** O que a pessoa digita para pedir o link de redefinicao. */
export type PedidoDeRedefinicao = Esquemas['CorpoDeRecuperacao']

/** A unica resposta da recuperacao — a mesma para e-mail cadastrado e nao cadastrado. */
export type RespostaDeRecuperacao = Esquemas['RespostaDeRecuperacao']

/** O token que veio na query do link, mais a senha nova. */
export type NovaSenha = Esquemas['CorpoDeRedefinicao']

/**
 * Pede o link de redefinicao (`202`).
 *
 * A resposta e deliberadamente identica para e-mail com e sem conta: **nao** use este
 * retorno para dizer a alguem se o e-mail existe — o backend gastou o mesmo tempo e
 * devolveu o mesmo corpo justamente para que isso seja impossivel. A tela mostra a
 * `mensagem` como veio.
 */
export function solicitarRedefinicaoDeSenha(
  pedido: PedidoDeRedefinicao,
): Promise<RespostaDeRecuperacao> {
  return chamar<RespostaDeRecuperacao>('identidade', '/senha/recuperacao', {
    method: 'POST',
    corpo: pedido,
    semRenovacao: true,
  })
}

/**
 * Redefine a senha com o token do link (`204`).
 *
 * Nao autentica ninguem: a redefinicao revoga **todas** as sessoes do usuario, entao a
 * tela seguinte e a de login. Link invalido, expirado ou ja usado sobem como `ErroDaApi`
 * `400` com a mesma mensagem — nao ha como distinguir os casos, e nem deve haver.
 */
export function redefinirSenha(nova: NovaSenha): Promise<void> {
  return chamar<void>('identidade', '/senha/redefinicao', {
    method: 'POST',
    corpo: nova,
    semRenovacao: true,
  })
}

/**
 * Um usuario do CRM, como as tres rotas de gestao o devolvem. Sem senha, sem hash.
 *
 * `criado_em` e `timestamptz` em UTC, ISO 8601 — a exibicao em `America/Sao_Paulo` e da tela.
 */
export type Usuario = Esquemas['RespostaDeUsuario']

/** O que o Admin digita para criar alguem. `perfil_id` e a linha do catalogo de perfis (AD-18). */
export type NovoUsuario = Esquemas['CorpoDeNovoUsuario']

/**
 * Cria um usuario (`201`). So o Admin alcanca esta rota.
 *
 * A pessoa nasce ativa, com a senha inicial que o Admin digitou, e entra pelo `entrar`
 * na hora — com as **permissoes do perfil**, lidas na emissao do JWT. Nao existe permissao
 * por usuario (11.1): quem precisa de algo diferente ganha um perfil personalizado.
 *
 * Recusas em PT-BR, como `ErroDaApi`: `409` e-mail ja cadastrado, `400` senha abaixo do
 * minimo (a mensagem **nomeia** o minimo), `422` nome ou e-mail vazio, perfil inexistente
 * ou campo acima do teto (`nome` 150, `email` 320, `senha` 1024), `403` quem nao e
 * Admin, `401` o Admin que foi desativado enquanto usava a tela.
 *
 * Os quatro campos vao **um a um**, e nao o objeto que chegou: o corpo do servico e
 * fechado (`extra="forbid"`), entao um objeto de formulario com um campo a mais — um
 * `confirmarSenha`, por exemplo — viraria um `422` generico que nao diz qual campo
 * sobrou. A tipagem estrutural do TypeScript nao pega isso quando o objeto vem de uma
 * variavel.
 */
export function criarUsuario({ nome, email, senha, perfil_id }: NovoUsuario): Promise<Usuario> {
  return chamar<Usuario>('identidade', '/usuarios', {
    method: 'POST',
    corpo: { nome, email, senha, perfil_id },
  })
}

/**
 * Lista todos os usuarios (`200`) — **inclusive os desativados**, na ordem de criacao.
 *
 * O desativado continua na lista com o nome preservado: a linha nunca e apagada (FR-65),
 * e e dela que o historico dos epicos seguintes tira o nome de quem criou cada registro.
 * Sem paginacao, busca ou ordenacao: a equipe comercial cabe numa resposta.
 */
export function listarUsuarios(): Promise<Usuario[]> {
  return chamar<Usuario[]>('identidade', '/usuarios')
}

/**
 * Desativa um usuario (`200`), devolvendo-o com `ativo: false`.
 *
 * As sessoes dele caem na mesma transacao: a renovacao dele passa a devolver `401` e o
 * login e recusado. O JWT que ele ja tem continua valendo por ate 15 minutos (AD-5) para
 * o resto do CRM — nao ha como o SPA encurtar isso, e nao ha reativacao. **As tres rotas
 * de `/usuarios` sao a excecao**: elas reconferem no banco se o autor ainda esta ativo, e
 * respondem `401` na chamada seguinte.
 *
 * **Se o alvo for o proprio Admin autenticado**, a tela precisa chamar `sair()` e mandar a
 * pessoa para o login logo depois do `200`: sem isso a propria tela de usuarios passa a
 * responder `401` no clique seguinte, e o resto do CRM continua aberto por ate quinze
 * minutos — dois comportamentos diferentes na mesma sessao, nenhum deles explicado a
 * pessoa.
 *
 * **Idempotente**: desativar quem ja esta desativado devolve o mesmo corpo. `404` quando
 * o `id` nao existe e `409` quando o alvo e o ultimo Admin ativo.
 *
 * O `id` vai por `encodeURIComponent`: ele chega da listagem, mas montar caminho com
 * interpolacao crua e o tipo de coisa que so falha no dia em que o valor deixa de ser um
 * uuid.
 */
export function desativarUsuario(id: string): Promise<Usuario> {
  return chamar<Usuario>('identidade', `/usuarios/${encodeURIComponent(id)}/desativacao`, {
    method: 'POST',
  })
}

/* --- Perfis e permissoes por perfil (historia 11.1) --------------------------------- */

/** O escopo de carteira do perfil (AD-4): `todas` e o que o SPA chama de gestao. */
export type EscopoDeCarteira = Esquemas['EscopoDeCarteira']

/**
 * Um perfil do catalogo — de fabrica ou personalizado — com o mapa inteiro, como
 * `GET /perfis` o devolve. A matriz vem agrupada nas oito areas do FR-61-A, com rotulo,
 * estado, a marca ✳ (`limitada`, derivada de concedida + escopo `propria`), os
 * `estados_disponiveis` (o teto) e, quando a linha nao e ligavel, o `motivo_do_teto`.
 */
export type PerfilDoCatalogo = Esquemas['RespostaDePerfil']

/** Uma das oito areas do FR-61-A, com as linhas dela na ordem do catalogo. */
export type AreaDoMapa = Esquemas['RespostaDeAreaDoMapa']

/** Uma permissao do catalogo, para um perfil. */
export type LinhaDoMapa = Esquemas['RespostaDeLinhaDoMapa']

/** O corpo da criacao de um perfil personalizado: nome, base e escopo — os dois ultimos fixos. */
export type NovoPerfil = Esquemas['CorpoDeNovoPerfil']

/** O mapa completo `nome -> estado` que o `PUT` recebe: as 40 (desde a 13.11b), sempre completas. */
export type MapaDePermissoes = Esquemas['CorpoDePermissoesDoPerfil']['permissoes']

/** Chave de cache do catalogo de perfis. */
export const CHAVE_DOS_PERFIS = ['identidade', 'perfis'] as const

/** Lista os perfis (`200`): os quatro de fabrica e os personalizados. So o admin de fabrica. */
export function listarPerfis(): Promise<PerfilDoCatalogo[]> {
  return chamar<PerfilDoCatalogo[]>('identidade', '/perfis')
}

/**
 * Cria um perfil personalizado (`201`). O mapa nasce **copia do base**, grampeado no teto
 * do escopo escolhido. O escopo nunca muda depois. `422` nome repetido ou vazio.
 */
export function criarPerfil({
  nome,
  perfil_base,
  escopo_de_carteira,
}: NovoPerfil): Promise<PerfilDoCatalogo> {
  return chamar<PerfilDoCatalogo>('identidade', '/perfis', {
    method: 'POST',
    corpo: { nome, perfil_base, escopo_de_carteira },
  })
}

/** Renomeia um personalizado (`200`). Os de fabrica tem nome fixo (`422`). */
export function renomearPerfil(id: string, nome: string): Promise<PerfilDoCatalogo> {
  return chamar<PerfilDoCatalogo>('identidade', `/perfis/${encodeURIComponent(id)}`, {
    method: 'PUT',
    corpo: { nome },
  })
}

/**
 * Grava o mapa **completo** do perfil (`200`). Vale para todos os usuarios dele na
 * proxima renovacao do JWT (ate 15 minutos, AD-5). `400` estado acima do teto; `422` mapa
 * incompleto — as duas mensagens sao do servidor e a tela as mostra como vieram.
 */
export function definirPermissoesDoPerfil(
  id: string,
  permissoes: MapaDePermissoes,
): Promise<PerfilDoCatalogo> {
  return chamar<PerfilDoCatalogo>('identidade', `/perfis/${encodeURIComponent(id)}/permissoes`, {
    method: 'PUT',
    corpo: { permissoes },
  })
}

/** Exclui um personalizado sem usuarios (`204`). De fabrica ou com usuarios: `409`. */
export function excluirPerfil(id: string): Promise<void> {
  return chamar<void>('identidade', `/perfis/${encodeURIComponent(id)}`, { method: 'DELETE' })
}

/**
 * Troca o perfil de um usuario (`200`). As sessoes dele caem **na mesma transacao**
 * (AD-5): a pessoa precisa entrar de novo. `422` perfil inexistente; `409` quando tiraria o
 * ultimo Admin ativo do perfil Admin.
 */
export function trocarPerfilDoUsuario(id: string, perfilId: string): Promise<Usuario> {
  return chamar<Usuario>('identidade', `/usuarios/${encodeURIComponent(id)}/perfil`, {
    method: 'PUT',
    corpo: { perfil_id: perfilId },
  })
}

/** Achata o mapa de um perfil no `nome -> estado` completo que o `PUT` exige. */
export function mapaDoPerfil(perfil: PerfilDoCatalogo): MapaDePermissoes {
  return Object.fromEntries(
    perfil.areas.flatMap((area) => area.linhas.map((linha) => [linha.permissao, linha.estado])),
  )
}

/**
 * O admin **de fabrica**: `perfil === 'admin'` e `perfil_id` nulo (AD-18). E o unico que
 * gere usuarios e perfis — um personalizado derivado de admin nao. Conveniencia de tela: a
 * guarda de verdade e a do servico.
 */
export function ehAdminDeFabrica(sessao: Pick<Sessao, 'perfil' | 'perfil_id'>): boolean {
  return sessao.perfil === 'admin' && sessao.perfil_id === null
}

/** O catalogo de perfis, para as telas Usuarios e Permissoes. */
export function usePerfis({ habilitada = true }: { habilitada?: boolean } = {}) {
  return useQuery({
    queryKey: CHAVE_DOS_PERFIS,
    queryFn: listarPerfis,
    enabled: habilitada,
    retry: false,
  })
}

/**
 * Um registro da trilha de auditoria, como `GET /auditoria` o devolve.
 *
 * `acao` e o nome no catalogo do servico (`usuario_criado`, `usuario_desativado`, …);
 * `autor_nome` e nulo quando o autor nao tem mais linha em `usuario` — a trilha nao esconde
 * registro. `autor_perfil` e o perfil do autor **no momento do ato**, e nao o de hoje.
 * `detalhe` e o "o que mudou": nunca senha, hash, token, custo, margem ou valor.
 * `criada_em` e `timestamptz` em UTC, ISO 8601 — a exibicao em `America/Sao_Paulo` e da tela.
 */
export type RegistroDeAuditoria = Esquemas['RespostaDeRegistro']

/**
 * Quantos registros o backend devolve quando `limite` **nao** e enviado.
 *
 * Exportado porque a tela precisa deste numero para saber que chegou ao fim da trilha: uma
 * pagina com menos itens que o limite em vigor e a ultima, e sem enviar `limite` o
 * chamador nao teria com o que comparar. Espelha `LIMITE_PADRAO` de
 * `servicos/identidade/aplicacao/auditoria.py`, que e pinado la por
 * `test_o_limite_padrao_e_o_numero_que_o_cliente_do_frontend_espelha` — os dois numeros
 * mudam juntos ou nao mudam, porque nenhum schema gerado liga um ao outro.
 */
export const LIMITE_PADRAO_DA_TRILHA = 100

/** O que a paginacao da trilha aceita. Os dois sao opcionais. */
export interface FiltroDaTrilha {
  /**
   * De 1 a 500; o backend usa `LIMITE_PADRAO_DA_TRILHA` quando nao vem. Fora da faixa e
   * `422`. Valor que nao e inteiro (um `NaN` vindo de `Number(campo)`) e **omitido**, e
   * nao enviado como texto invalido.
   */
  limite?: number
  /**
   * O `id` da ultima linha ja lida: a pagina seguinte traz so o que vem antes dela.
   * String vazia e **omitida** — e a saida natural de `pagina.at(-1)?.id` numa pagina
   * vazia, ou de um estado iniciado em `''`, e enviar `antes_de=` renderia um `422`.
   */
  antesDe?: string
}

/**
 * Le a trilha de auditoria (`200`), do mais novo para o mais antigo. So o Admin a alcanca.
 *
 * **Ha limite e cursor aqui, ao contrario de `listarUsuarios`**, porque a trilha cresce a
 * cada ato administrativo e nunca encolhe (ela e append-only). O cursor e o proprio `id`:
 * uuid v7 e cronologico por construcao, entao a pagina seguinte e
 * `lerTrilhaDeAuditoria({ antesDe: pagina[pagina.length - 1].id })`. Uma resposta com
 * menos itens que o limite em vigor — o que voce enviou, ou `LIMITE_PADRAO_DA_TRILHA` — e
 * o fim da trilha.
 *
 * **Parametro vazio ou invalido e omitido, e nao enviado.** `antesDe: ''` e `limite: NaN`
 * sao as saidas naturais de um estado de tela ainda sem valor (`pagina.at(-1)?.id` numa
 * lista vazia, `Number(campo)` num campo em branco); envia-los como `antes_de=` e
 * `limite=NaN` renderia um `422` que a tela nao tem como explicar a ninguem. Omitir cai no
 * comportamento certo: a primeira pagina, no limite padrao.
 *
 * O que **nao** existe e o resto do CRUD: nao ha funcao de criar, alterar ou apagar
 * registro aqui, e nao ha rota para isso do outro lado. A tabela e append-only por
 * construcao — gatilho no banco, inclusive contra o superusuario.
 *
 * `422` quando `limite` sai de `[1, 500]` ou `antesDe` nao e uuid; `403` para quem nao e
 * Admin; `401` sem sessao — ou quando o proprio Admin foi desativado ou rebaixado.
 */
export function lerTrilhaDeAuditoria(opcoes: FiltroDaTrilha = {}): Promise<RegistroDeAuditoria[]> {
  const parametros = new URLSearchParams()
  if (opcoes.limite !== undefined && Number.isInteger(opcoes.limite)) {
    parametros.set('limite', String(opcoes.limite))
  }
  if (opcoes.antesDe !== undefined && opcoes.antesDe !== '') {
    parametros.set('antes_de', opcoes.antesDe)
  }
  const consulta = parametros.toString()
  return chamar<RegistroDeAuditoria[]>(
    'identidade',
    consulta === '' ? '/auditoria' : `/auditoria?${consulta}`,
  )
}

/* --- Quem pode ser Responsavel (historia 2.5) ------------------------------------- */

/**
 * Um possivel Responsavel — quem a Empresa (e, no epico 3, o Negocio) pode ter como dono.
 *
 * **Sem `email` e sem `criado_em`**, ao contrario de `Usuario`: o response model do servico e
 * enxuto de proposito (AD-3) — quem escolhe um Responsavel nao precisa do e-mail de ninguem.
 * `ativo` vem junto porque o FR-65 manda o nome do desligado continuar aparecendo: a tela
 * precisa **nomear** quem ja nao pode ser escolhido.
 */
export type Responsavel = Esquemas['RespostaDeResponsavel']

/**
 * Chave de cache da lista de possiveis Responsaveis. Uma so, como `CHAVE_DAS_OPCOES` do
 * cadastro: a resposta e a equipe inteira, muda raramente, e a tela de Empresas (2.6) vai ler a
 * mesma entrada em vez de pagar uma ida a rede por ficha listada.
 */
export const CHAVE_DOS_RESPONSAVEIS = ['identidade', 'responsaveis'] as const

/**
 * Lista quem pode ser Responsavel (`200`) — **inclusive os desativados**, na ordem de criacao.
 *
 * E rota propria, e nao `listarUsuarios`: aquela e fechada ao Admin **e** reconfere no banco se
 * o autor ainda e Admin ativo, entao ela responde `401` para os outros tres perfis. O FR-32 diz
 * que qualquer usuario troca o Responsavel, e sem esta lista o seletor so teria uuid a oferecer.
 *
 * `401` sem sessao, e mais nada: nao ha `403` aqui, porque nao ha permissao do FR-61-A para
 * "ver os nomes da equipe" — a permissao que existe e a de **trocar**, exigida na rota do
 * `cadastro` que troca.
 */
export function listarResponsaveis(): Promise<Responsavel[]> {
  return chamar<Responsavel[]>('identidade', '/usuarios/responsaveis')
}

/**
 * A equipe, para os seletores de Responsavel.
 *
 * `retry: false` como as demais consultas do CRM: a recusa esperada e o `401` de sessao
 * ausente, que nao muda por insistir — repetir so atrasaria o que a tela ja sabe.
 */
export function useResponsaveis() {
  return useQuery({
    queryKey: CHAVE_DOS_RESPONSAVEIS,
    queryFn: listarResponsaveis,
    retry: false,
  })
}

/* --- A prontidao de go-live (historia 9.2) ---------------------------------------- */

/**
 * Chave de cache da lista de usuarios — **propria**, e nao a dos possiveis Responsaveis.
 *
 * Sao duas rotas diferentes, com dois recortes diferentes: `GET /usuarios` e fechada ao Admin e
 * devolve `RespostaDeUsuario` (com e-mail e `criado_em`); `GET /usuarios/responsaveis` atende
 * qualquer perfil e devolve o recorte enxuto do FR-32. Compartilhar a chave faria o seletor de
 * Responsavel de qualquer tela servir — ou pedir — o corpo largo que so o Admin pode ver.
 */
export const CHAVE_DOS_USUARIOS = ['identidade', 'usuarios'] as const

/**
 * A equipe inteira, por perfil e por estado (`ativo`).
 *
 * Ela e o hook de `listarUsuarios`, que ate a 9.2 nao tinha um: a tela de usuarios ainda nao
 * existe (1.5), e quem consome a lista hoje e o cartao de prontidao de go-live — que precisa
 * saber **quais perfis nao tem ninguem ativo**.
 *
 * `retry: false` como as demais consultas do CRM: a recusa esperada e o `401`/`403` de quem nao e
 * Admin, que nao muda por insistir.
 *
 * **`habilitada` fecha a porta antes da rede**: o cartao devolve `null` para quem nao e Admin, e
 * um hook roda **antes** de qualquer `return null` do componente. Sem esta chave, todo Vendedor
 * que abrisse `/configuracao` pediria a lista de usuarios so para receber a recusa esperada.
 */
export function useUsuarios({ habilitada = true }: { habilitada?: boolean } = {}) {
  return useQuery({
    queryKey: CHAVE_DOS_USUARIOS,
    queryFn: listarUsuarios,
    enabled: habilitada,
    retry: false,
  })
}

/**
 * O backup diario tem destino externo configurado — **so o booleano**.
 *
 * O valor de `RCLONE_REMOTO_DO_BACKUP` nunca sai do servico: o nome do remoto e o endereco de
 * onde mora a copia de tudo. O que atravessa e `true`/`false`.
 */
export type ProntidaoDoBackup = Esquemas['RespostaDeProntidaoDoBackup']

/**
 * Chave de cache da prontidao do backup.
 *
 * **Propria**, e nao dentro da chave dos usuarios: sao duas rotas, e uma fonte fora do ar precisa
 * degradar **so** o item dela — que e a promessa do cartao. Uma chave compartilhada faria a falha
 * de uma leitura apagar a outra da tela.
 */
export const CHAVE_DA_PRONTIDAO_DO_BACKUP = ['identidade', 'prontidao'] as const

/**
 * Le se o backup diario tem destino externo (`200`).
 *
 * Este e o unico item do check de prontidao de go-live sem servico dono: ele e uma variavel de
 * ambiente do conteiner que copia, e a `identidade` — o servico de administracao — e quem a
 * publica como booleano. So Admin; os outros tres perfis recebem `403`.
 */
export function lerProntidaoDoBackup(): Promise<ProntidaoDoBackup> {
  return chamar<ProntidaoDoBackup>('identidade', '/prontidao')
}

/**
 * A prontidao do backup, para o cartao de go-live (9.2).
 *
 * `retry: false` e `habilitada` pelas mesmas razoes de `useUsuarios`, logo acima.
 */
export function useProntidaoDoBackup({ habilitada = true }: { habilitada?: boolean } = {}) {
  return useQuery({
    queryKey: CHAVE_DA_PRONTIDAO_DO_BACKUP,
    queryFn: lerProntidaoDoBackup,
    enabled: habilitada,
    retry: false,
  })
}
